import React, { useEffect, useMemo, useState } from "react";
import { AuthRequest, AuthRequestClient } from "../RequestAuth.types";
import { getUser } from "../../../services/utils";
import { api } from "../../../services/api";
import { useAdminUsers } from "../../manageAdmin/context/AdminUsersContext";
import { AdminPermission } from "../../manageAdmin/AdminUserList.types";

export type AuthRequestProviderProps = {
  authRequests: Array<AuthRequest>;
  allPatients: Array<AuthRequestClient>;
  pointPersons: Array<{ id: string; name: string; email: string; userType: string }>;
  refreshRequests: () => {};
  fetchPointPersons: () => void;
}

export const RequestAuthContext = React.createContext<AuthRequestProviderProps>({
  authRequests: [],
  allPatients: [],
  pointPersons: [],
  refreshRequests: () => ({}),
  fetchPointPersons: () => {},
});

export function AuthRequestProvider({ children }) {
  const { myPermissions } = useAdminUsers();
  const user = getUser();
  const [requests, setRequests] = useState<Array<AuthRequest>>([]);
  const [allPatients, setAllPatients] = useState<Array<AuthRequestClient>>([]);
  const [pointPersons, setPointPersons] = useState<Array<{ id: string; name: string; email: string; userType: string }>>([]);
  const [pointPersonsFetched, setPointPersonsFetched] = useState(false);

  const fetchRequests = useMemo(() => async () => {
    if (myPermissions?.includes(AdminPermission.READ_PAGE_MANAGE_AUTH_REQUEST)) {
      setRequests(await api.getAuthRequestsAsAdmin())
    }
  }, [myPermissions]);

  const fetchAllPatients = useMemo(() => async () => {
    if (myPermissions?.includes(AdminPermission.READ_PAGE_MANAGE_AUTH_REQUEST)) {
      setAllPatients(await api.getAllPatients())
    }
  }, [myPermissions]);

  const fetchPointPersons = useMemo(() => async () => {
    // Only fetch if we have permission and haven't fetched yet (memoization)
    if (myPermissions?.includes(AdminPermission.READ_PAGE_MANAGE_AUTH_REQUEST) && !pointPersonsFetched) {
      try {
        const data = await api.getAuthRequestPointPersons();
        setPointPersons(data);
        setPointPersonsFetched(true);
      } catch (error) {
        console.error('Failed to fetch point persons:', error);
      }
    }
  }, [myPermissions, pointPersonsFetched]);

  useEffect(() => {
    if (user?.id) {
      fetchRequests();
      fetchAllPatients();
      fetchPointPersons(); // Fetch point persons when landing on auth requests page
    }
  }, [user?.id, myPermissions]);

  const values = useMemo(() => ({
    authRequests: requests,
    refreshRequests: fetchRequests,
    allPatients: allPatients,
    pointPersons: pointPersons,
    fetchPointPersons: fetchPointPersons
  }), [requests, fetchRequests, allPatients, pointPersons, fetchPointPersons]);

  return <RequestAuthContext.Provider value={values}>{children}</RequestAuthContext.Provider>;
}

export function useAuthRequests() {
  const context = React.useContext<AuthRequestProviderProps>(RequestAuthContext);
  if (context === undefined) {
    throw new Error("useAuthRequests must be used within a AuthRequestProvider");
  }
  return context;
}
